/**
 * AccessibilityManager - Manages accessibility features and compliance
 */

export class AccessibilityManager {
  constructor() {
    this.uiController = null;
    this.mapController = null;
    this.isHighContrastEnabled = false;
    this.isScreenReaderMode = false;
    this.keyboardNavigation = null;
    this.announcementQueue = [];
  }

  /**
   * Set UI controller reference
   */
  setUIController(uiController) {
    this.uiController = uiController;
  }

  /**
   * Set map controller reference
   */
  setMapController(mapController) {
    this.mapController = mapController;
  }

  /**
   * Initialize accessibility features
   */
  initialize() {
    this.detectScreenReader();
    this.setupKeyboardNavigation();
    this.setupAriaLive();
    this.loadAccessibilityPreferences();
    this.enhanceTabOrder();
    this.setupFocusManagement();

    console.log('Accessibility manager initialized');
  }

  /**
   * Detect if screen reader is being used
   */
  detectScreenReader() {
    // Check for common screen reader indicators
    const indicators = [
      navigator.userAgent.includes('JAWS'),
      navigator.userAgent.includes('NVDA'),
      navigator.userAgent.includes('VoiceOver'),
      window.speechSynthesis && window.speechSynthesis.speaking
    ];

    this.isScreenReaderMode = indicators.some(Boolean);

    if (this.isScreenReaderMode) {
      document.body.classList.add('screen-reader-mode');
      this.enableScreenReaderOptimizations();
    }
  }

  /**
   * Set up keyboard navigation
   */
  setupKeyboardNavigation() {
    this.keyboardNavigation = {
      currentCellIndex: 0,
      gridCells: []
    };

    document.addEventListener('keydown', (event) => {
      this.handleKeyboardNavigation(event);
    });

    // Update grid cells reference when map is ready
    if (this.mapController) {
      setTimeout(() => {
        this.updateNavigableCells();
      }, 1000);
    }
  }

  /**
   * Handle keyboard navigation
   */
  handleKeyboardNavigation(event) {
    const activeElement = document.activeElement;

    // Grid navigation with arrow keys
    if (activeElement && activeElement.getAttribute('role') === 'button') {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          this.navigateGrid('up');
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.navigateGrid('down');
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.navigateGrid('left');
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.navigateGrid('right');
          break;
        case 'Home':
          event.preventDefault();
          this.navigateToFirstCell();
          break;
        case 'End':
          event.preventDefault();
          this.navigateToLastCell();
          break;
      }
    }

    // Skip to main content
    if (event.key === 'Tab' && event.shiftKey && event.ctrlKey) {
      event.preventDefault();
      this.skipToMainContent();
    }

    // Quick access shortcuts
    if (event.altKey && !event.ctrlKey && !event.shiftKey) {
      switch (event.key) {
        case 'm':
          event.preventDefault();
          this.focusOnMap();
          break;
        case 'i':
          event.preventDefault();
          this.focusOnInfoPanel();
          break;
        case 'c':
          event.preventDefault();
          this.focusOnControls();
          break;
      }
    }
  }

  /**
   * Navigate grid with keyboard
   */
  navigateGrid(direction) {
    if (!this.keyboardNavigation.gridCells.length) {
      this.updateNavigableCells();
      return;
    }

    const currentIndex = this.keyboardNavigation.currentCellIndex;
    const gridWidth = 12; // 360° / 30° = 12 columns
    let newIndex = currentIndex;

    switch (direction) {
      case 'up':
        newIndex = currentIndex - gridWidth;
        break;
      case 'down':
        newIndex = currentIndex + gridWidth;
        break;
      case 'left':
        newIndex = currentIndex - 1;
        break;
      case 'right':
        newIndex = currentIndex + 1;
        break;
    }

    // Wrap around edges
    if (newIndex < 0) {
      newIndex = this.keyboardNavigation.gridCells.length + newIndex;
    } else if (newIndex >= this.keyboardNavigation.gridCells.length) {
      newIndex = newIndex - this.keyboardNavigation.gridCells.length;
    }

    this.focusOnCell(newIndex);
  }

  /**
   * Focus on a specific grid cell
   */
  focusOnCell(index) {
    const cells = this.keyboardNavigation.gridCells;
    if (index >= 0 && index < cells.length) {
      this.keyboardNavigation.currentCellIndex = index;
      const cell = cells[index];
      if (cell._path) {
        cell._path.focus();
        this.announceCell(cell);
      }
    }
  }

  /**
   * Update navigable cells array
   */
  updateNavigableCells() {
    if (!this.mapController || !this.mapController.gridLayer) return;

    this.keyboardNavigation.gridCells = [];

    this.mapController.gridLayer.eachLayer(layer => {
      if (layer._path) {
        this.keyboardNavigation.gridCells.push(layer);
      }
    });

    console.log(`Updated ${this.keyboardNavigation.gridCells.length} navigable cells`);
  }

  /**
   * Navigate to first cell
   */
  navigateToFirstCell() {
    this.focusOnCell(0);
  }

  /**
   * Navigate to last cell
   */
  navigateToLastCell() {
    this.focusOnCell(this.keyboardNavigation.gridCells.length - 1);
  }

  /**
   * Focus on map element
   */
  focusOnMap() {
    const mapEl = document.getElementById('map');
    if (mapEl) {
      mapEl.focus();
      this.announce('Focused on map');
    }
  }

  /**
   * Focus on info panel
   */
  focusOnInfoPanel() {
    const panelEl = document.getElementById('info-panel');
    if (panelEl) {
      panelEl.focus();
      this.announce('Focused on information panel');
    }
  }

  /**
   * Focus on controls
   */
  focusOnControls() {
    const controlsEl = document.getElementById('controls');
    if (controlsEl) {
      const firstButton = controlsEl.querySelector('button');
      if (firstButton) {
        firstButton.focus();
        this.announce('Focused on controls');
      }
    }
  }

  /**
   * Skip to main content
   */
  skipToMainContent() {
    const mapEl = document.getElementById('map');
    if (mapEl) {
      mapEl.focus();
      this.announce('Skipped to main content');
    }
  }

  /**
   * Set up ARIA live regions
   */
  setupAriaLive() {
    // Create announcement region if it doesn't exist
    if (!document.getElementById('aria-announcements')) {
      const announcer = document.createElement('div');
      announcer.id = 'aria-announcements';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.style.cssText = `
        position: absolute;
        left: -10000px;
        width: 1px;
        height: 1px;
        overflow: hidden;
      `;
      document.body.appendChild(announcer);
    }

    // Create assertive announcer for urgent messages
    if (!document.getElementById('aria-announcements-assertive')) {
      const assertiveAnnouncer = document.createElement('div');
      assertiveAnnouncer.id = 'aria-announcements-assertive';
      assertiveAnnouncer.setAttribute('aria-live', 'assertive');
      assertiveAnnouncer.setAttribute('aria-atomic', 'true');
      assertiveAnnouncer.style.cssText = `
        position: absolute;
        left: -10000px;
        width: 1px;
        height: 1px;
        overflow: hidden;
      `;
      document.body.appendChild(assertiveAnnouncer);
    }
  }

  /**
   * Announce text to screen readers
   */
  announce(text, urgent = false) {
    const announcerId = urgent ? 'aria-announcements-assertive' : 'aria-announcements';
    const announcer = document.getElementById(announcerId);

    if (announcer) {
      // Clear previous announcement
      announcer.textContent = '';

      // Set new announcement after a brief delay
      setTimeout(() => {
        announcer.textContent = text;
      }, 100);

      // Queue announcement for logging
      this.announcementQueue.push({
        text,
        urgent,
        timestamp: Date.now()
      });

      // Keep only last 20 announcements
      if (this.announcementQueue.length > 20) {
        this.announcementQueue = this.announcementQueue.slice(-20);
      }
    }

    console.log(`[A11y] ${urgent ? 'URGENT: ' : ''}${text}`);
  }

  /**
   * Announce cell information
   */
  announceCell(cell) {
    if (cell.cellData) {
      const { lat, lon, biome, description } = cell.cellData;
      const announcement = `${biome} biome at latitude ${lat.toFixed(1)}, longitude ${lon.toFixed(1)}. ${description}`;
      this.announce(announcement);
    }
  }

  /**
   * Toggle high contrast mode
   */
  toggleHighContrast() {
    this.isHighContrastEnabled = !this.isHighContrastEnabled;
    document.body.classList.toggle('high-contrast', this.isHighContrastEnabled);

    // Save preference
    localStorage.setItem('landgrid-high-contrast', this.isHighContrastEnabled);

    this.announce(`High contrast mode ${this.isHighContrastEnabled ? 'enabled' : 'disabled'}`);

    return this.isHighContrastEnabled;
  }

  /**
   * Load accessibility preferences
   */
  loadAccessibilityPreferences() {
    // Load high contrast preference
    const highContrast = localStorage.getItem('landgrid-high-contrast') === 'true';
    if (highContrast) {
      this.isHighContrastEnabled = true;
      document.body.classList.add('high-contrast');
    }

    // Check for reduced motion preference
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.body.classList.add('reduced-motion');
      this.announce('Reduced motion mode detected and enabled');
    }

    // Check for high contrast preference from OS
    if (window.matchMedia && window.matchMedia('(prefers-contrast: high)').matches) {
      this.toggleHighContrast();
    }
  }

  /**
   * Enhance tab order for better keyboard navigation
   */
  enhanceTabOrder() {
    // Set tab indices for better navigation flow
    const elements = [
      { selector: '.tab-button', startIndex: 10 },
      { selector: '.control-btn', startIndex: 20 },
      { selector: '.quiz-option', startIndex: 30 }
    ];

    elements.forEach(({ selector, startIndex }) => {
      document.querySelectorAll(selector).forEach((el, index) => {
        el.setAttribute('tabindex', startIndex + index);
      });
    });
  }

  /**
   * Set up focus management
   */
  setupFocusManagement() {
    // Trap focus in modals
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        const modal = document.querySelector('.error-dialog:not([hidden]), .help-modal');
        if (modal) {
          this.trapFocus(event, modal);
        }
      }
    });

    // Restore focus when modals close
    this.setupFocusRestore();
  }

  /**
   * Trap focus within a container
   */
  trapFocus(event, container) {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * Set up focus restoration
   */
  setupFocusRestore() {
    let lastFocusedElement = null;

    // Store focus before opening modals
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = mutation.target;
          if (target.classList.contains('error-dialog') || target.classList.contains('help-modal')) {
            if (target.style.display !== 'none' && !target.hidden) {
              lastFocusedElement = document.activeElement;
            } else if (lastFocusedElement) {
              lastFocusedElement.focus();
              lastFocusedElement = null;
            }
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['style', 'hidden']
    });
  }

  /**
   * Enable screen reader optimizations
   */
  enableScreenReaderOptimizations() {
    // Add more descriptive labels
    this.enhanceAriaLabels();

    // Provide additional context
    this.addContextualDescriptions();

    // Optimize announcement timing
    this.optimizeAnnouncementTiming();

    this.announce('Screen reader optimizations enabled', true);
  }

  /**
   * Enhance ARIA labels throughout the application
   */
  enhanceAriaLabels() {
    // Enhance map labels
    const mapEl = document.getElementById('map');
    if (mapEl) {
      mapEl.setAttribute('aria-describedby', 'map-description');

      // Create description if it doesn't exist
      if (!document.getElementById('map-description')) {
        const description = document.createElement('div');
        description.id = 'map-description';
        description.style.display = 'none';
        description.textContent = 'Interactive world map showing different biome regions. Use arrow keys to navigate between grid cells, Enter to select, and Tab to move between interface elements.';
        mapEl.parentNode.insertBefore(description, mapEl);
      }
    }

    // Enhance button labels
    document.querySelectorAll('button').forEach(button => {
      if (!button.getAttribute('aria-label') && !button.textContent.trim()) {
        const title = button.getAttribute('title');
        if (title) {
          button.setAttribute('aria-label', title);
        }
      }
    });
  }

  /**
   * Add contextual descriptions
   */
  addContextualDescriptions() {
    // Add descriptions for complex UI elements
    const elements = [
      {
        selector: '.legend',
        description: 'Map legend showing biome types and their corresponding colors'
      },
      {
        selector: '.info-panel',
        description: 'Information panel with exploration details, learning content, and quiz questions'
      },
      {
        selector: '.controls',
        description: 'Map controls for resetting view, taking screenshots, and accessibility options'
      }
    ];

    elements.forEach(({ selector, description }) => {
      const element = document.querySelector(selector);
      if (element && !element.getAttribute('aria-describedby')) {
        const descId = `desc-${Math.random().toString(36).substr(2, 9)}`;
        const descElement = document.createElement('div');
        descElement.id = descId;
        descElement.style.display = 'none';
        descElement.textContent = description;
        element.setAttribute('aria-describedby', descId);
        element.parentNode.insertBefore(descElement, element);
      }
    });
  }

  /**
   * Optimize announcement timing for screen readers
   */
  optimizeAnnouncementTiming() {
    // Debounce rapid announcements
    let announcementTimeout = null;

    const originalAnnounce = this.announce.bind(this);
    this.announce = (text, urgent = false) => {
      if (urgent) {
        originalAnnounce(text, urgent);
        return;
      }

      clearTimeout(announcementTimeout);
      announcementTimeout = setTimeout(() => {
        originalAnnounce(text, urgent);
      }, 500);
    };
  }

  /**
   * Get accessibility status
   */
  getAccessibilityStatus() {
    return {
      highContrast: this.isHighContrastEnabled,
      screenReader: this.isScreenReaderMode,
      reducedMotion: document.body.classList.contains('reduced-motion'),
      keyboardNavigation: !!this.keyboardNavigation,
      announcementCount: this.announcementQueue.length
    };
  }

  /**
   * Test accessibility features
   */
  testAccessibilityFeatures() {
    const results = {
      ariaLive: !!document.getElementById('aria-announcements'),
      keyboardNavigation: !!this.keyboardNavigation,
      focusManagement: true, // Assume working if no errors
      highContrast: this.isHighContrastEnabled,
      screenReaderOptimizations: this.isScreenReaderMode
    };

    console.log('Accessibility test results:', results);
    return results;
  }
}
